import { createTheme } from '@mui/material';

import { COLORS } from '../Constant/COLORS';
import FONTS from '../Constant/FONTS';

// ─────────────────────────────────────────────────────────────────────────────
// Elegant / premium-events theme.
// Deep-teal brand + warm charcoal ink on a warm off-white canvas, a serif
// display face (Playfair Display) for headings paired with Montserrat body,
// hairline borders in preference to heavy shadows, and restrained 8–12px radii.
//
// All of the original custom palette entries (blue / green / red / yellow / …)
// are preserved so existing screens that do `color="green"` keep working while
// pages are migrated phase by phase.
// ─────────────────────────────────────────────────────────────────────────────

// Layered cool-tinted shadows — soft ambient + a lift shadow for hover states.
const softShadow =
  '0 1px 2px rgba(14, 27, 31, 0.04), 0 6px 20px rgba(14, 27, 31, 0.06)';
const hoverShadow =
  '0 2px 6px rgba(14, 27, 31, 0.06), 0 18px 40px rgba(14, 27, 31, 0.12)';
const brandShadow = '0 8px 22px rgba(1, 64, 79, 0.22)';

const theme = createTheme({
  shape: {
    borderRadius: 12,
  },

  palette: {
    primary: {
      main: COLORS.teal,
      dark: COLORS.tealDark,
      light: COLORS.tealSoft,
      contrastText: '#ffffff',
    },
    secondary: {
      main: COLORS.green,
      dark: COLORS.green4,
      contrastText: '#ffffff',
    },
    error: { main: COLORS.red3, light: COLORS.red },
    success: { main: COLORS.green, dark: COLORS.green4 },
    warning: { main: COLORS.yellow },
    text: {
      primary: COLORS.ink,
      secondary: COLORS.inkSoft,
    },
    background: {
      default: COLORS.canvas,
      paper: COLORS.surface,
    },
    divider: COLORS.hairline,

    // Warm premium accent + convenience aliases.
    brass: { main: COLORS.brass, light: COLORS.brassSoft, contrastText: '#ffffff' },
    teal: { main: COLORS.teal, contrastText: '#ffffff' },

    // ── Legacy custom entries (kept for backward compatibility) ──
    grey3: { main: COLORS.grey3 },
    blue: { main: COLORS.blue, contrastText: '#ffffff' },
    red: { main: COLORS.red, contrastText: '#ffffff' },
    red3: { main: COLORS.red3, contrastText: '#ffffff' },
    green: { main: COLORS.green, contrastText: '#ffffff' },
    green3: { main: COLORS.green3 },
    green4: { main: COLORS.green4, contrastText: '#ffffff' },
    white1: { main: '#ffffff' },
    white3: { main: COLORS.white3 },
    yellow: { main: COLORS.yellow },
  },

  typography: {
    fontFamily: FONTS.MONT,
    allVariants: {
      fontFamily: FONTS.MONT,
      color: COLORS.ink,
    },
    // Serif display scale for headings — larger, tighter, more editorial. Uses
    // clamp() so the big display sizes scale fluidly down to mobile.
    h1: { fontFamily: FONTS.SERIF, fontWeight: 700, fontSize: 'clamp(2.5rem, 5vw, 3.75rem)', lineHeight: 1.05, letterSpacing: '-1px' },
    h2: { fontFamily: FONTS.SERIF, fontWeight: 700, fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', lineHeight: 1.1, letterSpacing: '-0.5px' },
    h3: { fontFamily: FONTS.SERIF, fontWeight: 700, fontSize: 'clamp(1.6rem, 2.6vw, 2.125rem)', lineHeight: 1.15, letterSpacing: '-0.3px' },
    h4: { fontFamily: FONTS.SERIF, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25, letterSpacing: '-0.2px' },
    h5: { fontFamily: FONTS.SERIF, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    // Small headings stay sans for UI clarity.
    h6: { fontFamily: FONTS.MONT, fontWeight: 700, fontSize: '1.0625rem', lineHeight: 1.4, letterSpacing: '0.1px' },
    subtitle1: { fontFamily: FONTS.MONT, fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle2: { fontFamily: FONTS.MONT, fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5 },
    body1: { fontFamily: FONTS.MONT, fontSize: '1rem', lineHeight: 1.65 },
    body2: { fontFamily: FONTS.MONT, fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontFamily: FONTS.MONT, fontWeight: 600, letterSpacing: '0.2px' },
    caption: { fontFamily: FONTS.MONT, fontSize: '0.75rem', lineHeight: 1.5, color: COLORS.inkSoft },
    overline: { fontFamily: FONTS.MONT, fontWeight: 600, fontSize: '0.6875rem', letterSpacing: '1.2px', textTransform: 'uppercase', color: COLORS.inkSoft },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: 'smooth', WebkitFontSmoothing: 'antialiased' },
        body: { backgroundColor: COLORS.canvas },
        '::selection': { backgroundColor: COLORS.tealSoft, color: COLORS.teal },
        // Slim cool scrollbar (WebKit).
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: COLORS.hairlineStrong,
          borderRadius: 8,
          border: `2px solid ${COLORS.canvas}`,
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: COLORS.inkFaint },
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.001ms !important',
            transitionDuration: '0.001ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState: { color, variant } }) => {
          const isBrandContained =
            (color === 'blue' ||
              color === 'green' ||
              color === 'yellow' ||
              color === 'red' ||
              color === 'primary' ||
              color === 'secondary') &&
            variant === 'contained';
          return {
            color: isBrandContained ? 'white' : COLORS.ink,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            padding: '9px 22px',
            boxShadow: 'none',
            lineHeight: 1.5,
            position: 'relative',
            overflow: 'hidden',
            transition:
              'background-color .2s ease, border-color .2s ease, color .2s ease, box-shadow .25s ease, transform .18s cubic-bezier(0.22,1,0.36,1)',
            '&:hover': {
              boxShadow: isBrandContained ? brandShadow : 'none',
              transform: 'translateY(-1.5px)',
            },
            '&:active': { transform: 'translateY(0)' },
            // Light sheen sweep on hover for contained brand buttons.
            ...(isBrandContained && {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-120%',
                width: '60%',
                height: '100%',
                background:
                  'linear-gradient(100deg, transparent, rgba(255,255,255,0.28), transparent)',
                transition: 'left .6s ease',
                pointerEvents: 'none',
              },
              '&:hover::after': { left: '130%' },
            }),
          };
        },
        outlined: {
          borderColor: COLORS.hairlineStrong,
          '&:hover': { borderColor: COLORS.teal, backgroundColor: COLORS.tealTint },
        },
        sizeLarge: { padding: '13px 30px', fontSize: '0.95rem', borderRadius: 12 },
        sizeSmall: { padding: '5px 14px' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.hairline}`,
          borderRadius: 16,
          boxShadow: softShadow,
          backgroundImage: 'none',
          transition:
            'box-shadow .28s cubic-bezier(0.22,1,0.36,1), transform .28s cubic-bezier(0.22,1,0.36,1), border-color .28s ease',
        },
      },
    },

    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          '&:hover': { transform: 'translateY(-4px)' },
          transition: 'transform .28s cubic-bezier(0.22,1,0.36,1)',
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // Default app bars get the light hairline-bottom treatment, but brand
        // variants (e.g. the professional area's teal header via color="blue")
        // keep their solid colour — so we branch on ownerState.color.
        root: ({ ownerState }) => {
          const isBrand =
            ownerState.color === 'blue' ||
            ownerState.color === 'primary' ||
            ownerState.color === 'teal';
          return {
            boxShadow: 'none',
            ...(isBrand
              ? { backgroundColor: COLORS.teal, color: '#ffffff' }
              : {
                  backgroundColor: COLORS.surface,
                  color: COLORS.ink,
                  borderBottom: `1px solid ${COLORS.hairline}`,
                }),
          };
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: COLORS.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.hairline },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.inkSoft },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.teal,
            borderWidth: '1.5px',
          },
        },
        input: { '&::placeholder': { color: COLORS.grey6, opacity: 1 } },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        hiddenLabel: true,
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600, fontFamily: FONTS.MONT },
        outlined: { borderColor: COLORS.hairline },
      },
    },

    MuiDialog: {
      defaultProps: { transitionDuration: { enter: 320, exit: 200 } },
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: `1px solid ${COLORS.hairline}`,
          boxShadow: '0 24px 70px rgba(14,27,31,0.22)',
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9,20,24,0.5)',
          backdropFilter: 'blur(3px)',
        },
        invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${COLORS.hairline}`,
          boxShadow: softShadow,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${COLORS.hairline}`, fontFamily: FONTS.MONT },
        head: {
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          color: COLORS.inkSoft,
          backgroundColor: COLORS.surfaceAlt,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: COLORS.hairline },
      },
    },

    // MUI X DataGrid (admin tables) — match the elegant table treatment.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.hairline}`,
          borderRadius: 12,
          backgroundColor: COLORS.surface,
          fontFamily: FONTS.MONT,
        },
        columnHeaders: {
          backgroundColor: COLORS.surfaceAlt,
          borderBottom: `1px solid ${COLORS.hairline}`,
        },
        columnHeaderTitle: {
          fontWeight: 700,
          fontSize: '0.72rem',
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          color: COLORS.inkSoft,
        },
        cell: { borderBottom: `1px solid ${COLORS.hairline}` },
        footerContainer: { borderTop: `1px solid ${COLORS.hairline}` },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: FONTS.MONT,
          minHeight: 48,
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variant: 'body2',
        color: COLORS.ink,
      },
    },

    MuiLink: {
      defaultProps: {
        underline: 'none',
      },
      styleOverrides: {
        root: {
          fontFamily: FONTS.MONT,
          cursor: 'pointer',
          color: COLORS.teal,
          fontWeight: 500,
          transition: 'color .2s ease',
          '&:hover': { color: COLORS.tealDark },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: COLORS.ink,
          fontFamily: FONTS.MONT,
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '6px 10px',
        },
        arrow: { color: COLORS.ink },
      },
    },
  },
});

export default theme;
